import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useMemo } from "react";
import usePushNotifications from "../hooks/usePushNotifications";

export default function DoctorLayout() {
  const [collapsed, setCollapsed] = useState(true);
  const navigate = useNavigate();

  // Read doctor info stored at login — used to show "Dr. X" in the header
  // so it's always clear which doctor's session is active.
  const doctorUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("doctorUser") || "null");
    } catch {
      return null;
    }
  }, []);

  /* ── Push notifications for doctor ── */
  const { supported: pushSupported, subscribed, loading: pushLoading,
          subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } =
    usePushNotifications("doctor");

  const logout = () => {
    sessionStorage.removeItem("doctorToken");
    sessionStorage.removeItem("doctorLoginDay");
    sessionStorage.removeItem("doctorUser");
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-slate-50">

      {/* TOP NAVBAR */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          {/* Hamburger on mobile */}
          <button
            className="md:hidden text-gray-500 text-xl"
            onClick={() => setCollapsed(c => !c)}
          >
            ☰
          </button>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-800">🏥 OPD Dashboard</span>
            {doctorUser?.name && (
              <>
                <span className="text-gray-300 hidden sm:inline">·</span>
                <span className="hidden sm:inline text-sm font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-lg">
                  {doctorUser.name}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Link back to public website */}
          <Link
            to="/"
            className="text-sm text-green-700 hover:text-green-900 hidden sm:inline-block"
          >
            ← Clinic Website
          </Link>

          {pushSupported && (
            <button
              onClick={subscribed ? pushUnsubscribe : pushSubscribe}
              disabled={pushLoading}
              title={subscribed ? "Disable push notifications" : "Enable push notifications"}
              className={`text-sm px-3 py-1.5 rounded-lg transition font-medium ${
                subscribed
                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {pushLoading ? "..." : subscribed ? "🔔 Notif On" : "🔕 Notif Off"}
            </button>
          )}
          <button
            onClick={logout}
            className="text-sm px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition"
          >
            Logout
          </button>
        </div>
      </header>

      {/* BODY */}
      <div className="flex pt-14 min-h-screen">

        {/* SIDEBAR */}
        <motion.aside
          animate={{ width: collapsed ? 64 : 240 }}
          transition={{ duration: 0.2 }}
          className="bg-white border-r shadow-sm flex flex-col shrink-0"
          style={{ height: "calc(100vh - 56px)", position: "sticky", top: "56px", overflow: "hidden" }}
        >
          {/* Toggle button + doctor name */}
          <div className="h-auto flex flex-col border-b">
            <div className="h-12 flex items-center justify-between px-3">
              {!collapsed && <span className="font-semibold text-sm text-gray-700">Doctor Panel</span>}
              <button
                onClick={() => setCollapsed(c => !c)}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500 ml-auto"
              >
                {collapsed ? "›" : "‹"}
              </button>
            </div>
            {/* Doctor name badge in sidebar (visible when expanded) */}
            {!collapsed && doctorUser?.name && (
              <div className="px-4 pb-3">
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-500 font-medium mb-0.5">Logged in as</p>
                  <p className="text-sm font-semibold text-blue-800 truncate">{doctorUser.name}</p>
                  <p className="text-xs text-blue-400 mt-0.5">Patients visible to you only</p>
                </div>
              </div>
            )}
          </div>

          {/* NAVIGATION */}
          <nav className="mt-1 flex-1">
            <NavItem collapsed={collapsed} icon="🏠" label="Dashboard"            to="/staff-portal/dashboard" />
            <NavItem collapsed={collapsed} icon="➕" label="New Patient"           to="/staff-portal/patient/new" />
            <NavItem collapsed={collapsed} icon="🔍" label="Returning Patient"     to="/staff-portal/patient/search" />
            <NavItem collapsed={collapsed} icon="⚙️" label="Prescription Settings" to="/staff-portal/prescription-settings" />
          </nav>

          {/* Bottom: back to site */}
          <div className="border-t pb-3 pt-2">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-2 text-xs text-gray-400 hover:text-green-700 hover:bg-gray-50 transition"
            >
              <span className="w-6 text-center">🌐</span>
              {!collapsed && <span>Clinic Website</span>}
            </Link>
          </div>
        </motion.aside>

        {/* CONTENT */}
        <main
          className="flex-1 p-6 overflow-y-auto"
          style={{ height: "calc(100vh - 56px)" }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/* NAV ITEM — uses NavLink for active highlight */
function NavItem({ label, to, collapsed, icon }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
          isActive
            ? "bg-blue-50 text-blue-700 font-medium border-r-2 border-blue-600"
            : "text-gray-600 hover:bg-gray-100"
        }`
      }
    >
      <span className="w-6 text-center text-base shrink-0">{icon || "•"}</span>
      {!collapsed && <span className="whitespace-nowrap">{label}</span>}
    </NavLink>
  );
}